import { ElementRef, Injectable, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { KeepAliveService } from '@frontend/sports/common/api-utils';
import { CasinoMiniGamesConfig } from '@frontend/sports/common/client-config-data-access';
import { CookieProvider } from '@frontend/sports/common/core/utils/cookie-provider';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UserService } from '@frontend/sports/user/feature';
import { AppInfoConfig, DeviceService, Page, SessionStoreService, WINDOW } from '@frontend/vanilla/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { ArcRestrictionService } from '../arc/arc-restriction.service';
import {
    CasinoEventType,
    CasinoWindowEvent,
    LobbyLoadedEvent,
    PingStatusGameOpenEvent,
    RecentCasinoGameCloseEvent,
    RecentCasinoGameOpenEvent,
    ShowLoginEvent,
    WindowScrollStartEvent,
} from './casino.event';
import { ChannelIds } from './minigames.model';
import {
    CasinoMessageData,
    MiniGamesWidgetCollapsedEvent,
    MiniGamesWidgetExpandedEvent,
    MiniGamesWidgetPostSessionKeyEvent,
    SportsWindowEvent,
} from './sports.event';

@Injectable({ providedIn: 'root' })
export class MiniGamesService implements OnDestroy {
    private page = inject(Page);
    private userService = inject(UserService);
    private appInfoConfig = inject(AppInfoConfig);
    private sessionStorage = inject(SessionStoreService);
    private cookieProvider = inject(CookieProvider);
    private casinoMiniGamesConfig = inject(CasinoMiniGamesConfig);
    private deviceService = inject(DeviceService);
    private keepAliveService = inject(KeepAliveService);
    private arcRestrictionService = inject(ArcRestrictionService);

    private casinoWindowEvents: Subject<CasinoWindowEvent> = new Subject<CasinoWindowEvent>();
    private recentCasinoGameOpenStorageKey = 'recentCasinoGameOpen';
    private darkModeCookieKey = 'dark-mode';

    private _miniGamesEnabled = new BehaviorSubject<boolean>(false);
    private hideMiniGamesByArc: boolean;
    get events(): Observable<CasinoWindowEvent> {
        return this.casinoWindowEvents;
    }

    get recentCasinoGameOpen(): string {
        return this.sessionStorage.get(this.recentCasinoGameOpenStorageKey) || '';
    }

    set recentCasinoGameOpen(recentCasinoGameOpen: string) {
        this.sessionStorage.set(this.recentCasinoGameOpenStorageKey, recentCasinoGameOpen);
    }

    get miniGamesEnabled(): Observable<boolean> {
        return this._miniGamesEnabled.asObservable();
    }

    private get isDarkMode(): boolean {
        return Number(this.cookieProvider.getCookie(this.darkModeCookieKey)) > 0;
    }

    constructor() {
        const productUtils = inject(SportsProductUtils);

        this.arcRestrictionService.gamingCrossSellEnabled$.pipe(productUtils.filterSportsEmitLast(), takeUntilDestroyed()).subscribe((state) => {
            this.hideMiniGamesByArc = state;
            this.areMiniGamesEnabled();
        });
        this._window.addEventListener('message', this.receiveMessage);
    }

    ngOnDestroy(): void {
        this._window.removeEventListener('message', this.receiveMessage);
    }

    resetOpenGame(): void {
        this.sessionStorage.set(this.recentCasinoGameOpenStorageKey, '');
    }

    private areMiniGamesEnabled(): void {
        if (this.hideMiniGamesByArc) {
            this._miniGamesEnabled.next(false);

            return;
        }
        if (!this.casinoMiniGamesConfig.areMiniGamesEnabled || !this.getUrl()) {
            this._miniGamesEnabled.next(false);

            return;
        }

        if (this.deviceService.isRobot) {
            this._miniGamesEnabled.next(false);

            return;
        }

        const userCountry = this.userService.isAuthenticated ? this.userService.country : this.userService.geoCountry;
        this._miniGamesEnabled.next(!this.isBlacklistedCountry(this.casinoMiniGamesConfig.blackListedCountries, userCountry));
    }

    sportsSpinnerLoadingDelayTime(): number {
        return this.casinoMiniGamesConfig.sportsSpinnerLoadingDelayTime;
    }

    getUrl(isKycVerified: boolean = false): string {
        let url = this.casinoMiniGamesConfig.url;
        if (url) {
            url = url.replace('{0}', this.getPageCulture());
            url = url.replace('{1}', this.appInfoConfig.brand);
            url = url.replace('{2}', this.getChanneId());
            url = url.replace('{3}', 'instantMini');
            url = url.replace('{4}', 'BETTING');
            url = url.replace('{5}', this.appInfoConfig.frontend);
            url = url.replace('{6}', this.userService.clientIP || '');
            url = url.replace('{7}', this.page.lang);
            url = url.replace('{8}', this.userService.currency);
            if (this.casinoMiniGamesConfig.appendSessionKey) {
                url = url.replace('{9}', this.userService.ssoToken as string);
            } else {
                url = url.replace('{9}', '');
            }
            url = url.replace('{10}', this.page.homePage);

            if (this.userService.isAuthenticated) {
                url = url.replace('{11}', this.appInfoConfig.frontend + '_' + this.userService.username);
                if (this.recentCasinoGameOpen) {
                    url = url + '&defaultlaunchgame=' + this.recentCasinoGameOpen;
                }
                url = url.replace('{13}', isKycVerified.toString());
            } else {
                url = url.replace('{11}', '');
                url = url.replace('{13}', isKycVerified.toString());
            }

            url = url.replace('{12}', this.isDarkMode.toString());
        }

        return String(url);
    }

    startKeepAlive(): void {
        this.keepAliveService.doRequest();
    }

    postEventsToCasino(event: SportsWindowEvent, casinoIframeRef: ElementRef): void {
        switch (event.constructor) {
            case MiniGamesWidgetExpandedEvent: {
                const messageData = new CasinoMessageData();
                messageData.type = 'sportsevent';
                messageData.data = { key: 'MiniGames', value: 'MiniGames_Expanded' };
                this.postMessage(messageData, casinoIframeRef);
                break;
            }
            case MiniGamesWidgetCollapsedEvent: {
                const messageData = new CasinoMessageData();
                messageData.type = 'sportsevent';
                messageData.data = { key: 'MiniGames', value: 'MiniGames_Collapsed' };
                this.postMessage(messageData, casinoIframeRef);
                break;
            }
            case MiniGamesWidgetPostSessionKeyEvent: {
                const messageData = new CasinoMessageData();
                messageData.type = 'sportsevent';
                messageData.data = { key: 'MiniGameSessionValue', value: this.userService.ssoToken as string };
                this.postMessage(messageData, casinoIframeRef);
                break;
            }
        }
    }

    private receiveMessage = (event: any) => {
        switch (event.data.type) {
            case CasinoEventType.LobbyLoaded: {
                this.triggerEvent(new LobbyLoadedEvent());
                break;
            }
            case CasinoEventType.RecentCasinoGameOpen: {
                const recentCasinoGameOpenEvent = new RecentCasinoGameOpenEvent();
                recentCasinoGameOpenEvent.gamename = event.data.data['gamename'];
                this.triggerEvent(recentCasinoGameOpenEvent);
                break;
            }
            case CasinoEventType.RecentCasinoGameClose: {
                this.triggerEvent(new RecentCasinoGameCloseEvent());
                break;
            }
            case CasinoEventType.ShowLogin: {
                const showLoginEvent = new ShowLoginEvent();
                showLoginEvent.gamename = event.data.data['gamename'];
                this.triggerEvent(showLoginEvent);
                break;
            }
            case CasinoEventType.PingStatusGameOpen: {
                this.triggerEvent(new PingStatusGameOpenEvent());
                break;
            }
            case CasinoEventType.WindowScrollStart: {
                const windowScrollStartEvent = new WindowScrollStartEvent();
                windowScrollStartEvent.data = event.data;
                this.triggerEvent(windowScrollStartEvent);
                break;
            }
        }
    };

    private triggerEvent(event: CasinoWindowEvent): void {
        this.casinoWindowEvents.next(event);
    }

    private getPageCulture(): string {
        return this.page.culture.replace('-', '_');
    }

    private getChanneId(): ChannelIds {
        if (!this.deviceService.isMobile) {
            return ChannelIds.WC;
        }

        if (this.deviceService.isiOS) {
            return ChannelIds.IOS;
        }

        return ChannelIds.Android;
    }

    private isBlacklistedCountry(blackListedCountries: string[], country: string | null): boolean {
        if (country) {
            return blackListedCountries.some((currentCountry) => currentCountry.toUpperCase() === country.toUpperCase());
        }

        return false;
    }

    private postMessage(casinoMessageData: CasinoMessageData, casinoIframeRef: ElementRef): void {
        casinoIframeRef.nativeElement.contentWindow.postMessage(casinoMessageData, '*');
    }

    private readonly _window = inject(WINDOW);
}
